import styled from '@emotion/styled';
import Modal from '@mui/material/Modal';
import type { FunctionComponent } from 'react';

import { ActionButtons } from './ActionButtons';
import { showFeedbackSuccessToast } from './FeedbackToaster';
import { useSkillFeedbackModalContext } from './ModalContext';
import { TabContent } from './TabContent';
import { TabsSwitcher } from './TabsSwitcher';
import { useFeedbackDialogStore } from './use-feedback-dialog-store';

const ModalContainer = styled.div({
    alignItems: 'center',
    background: '#fbfbff',
    borderRadius: '1rem',
    boxShadow: '-0.03125rem -0.03125rem 0px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    justifyContent: 'space-between',
    left: '50%',
    minHeight: '35rem',
    outline: 'none',
    padding: '1rem',
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '35rem',
});

const ContentContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    marginBottom: 'auto',
    width: '100%',
});

const HeaderContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    width: '100%',
});

const Title = styled.h2({
    color: '#040f16',
    fontFamily: 'FT Regola Neue',
    fontSize: '1.375rem',
    fontWeight: 500,
    lineHeight: '2rem',
    margin: 0,
});

const Description = styled.p({
    color: '#6e6e6c',
    fontFamily: 'FT Regola Neue',
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: '1.25rem',
    margin: 0,
});

export const SkillFeedbackModal: FunctionComponent = () => {
    const { closeFeedbackModal, isOpen } = useSkillFeedbackModalContext();
    const sendFeedback = useFeedbackDialogStore((state) => state.sendFeedback);
    const currentFeedbackType = useFeedbackDialogStore((state) => state.currentFeedbackType);
    const canSubmit = useFeedbackDialogStore((state) => state.canSubmit);
    const setFeedbackType = useFeedbackDialogStore((state) => state.setFeedbackType);
    const provideFeedbackTitleLabel = useFeedbackDialogStore((state) => state.provideFeedbackTitleLabel);
    const provideFeedbackDescriptionLabel = useFeedbackDialogStore((state) => state.provideFeedbackDescriptionLabel);

    const skillRequestSuccessTitleLabel = useFeedbackDialogStore((state) => state.skillRequestSuccessTitleLabel);
    const skillRequestSuccessMessageLabel = useFeedbackDialogStore((state) => state.skillRequestSuccessMessageLabel);
    const skillReportSuccessTitleLabel = useFeedbackDialogStore((state) => state.skillReportSuccessTitleLabel);
    const skillReportSuccessMessageLabel = useFeedbackDialogStore((state) => state.skillReportSuccessMessageLabel);
    const skillContentReportSuccessTitleLabel = useFeedbackDialogStore(
        (state) => state.skillContentReportSuccessTitleLabel,
    );
    const skillContentReportSuccessMessageLabel = useFeedbackDialogStore(
        (state) => state.skillContentReportSuccessMessageLabel,
    );

    const onSubmit = (): void => {
        sendFeedback();
        closeFeedbackModal();

        let successTitle: string;
        let successMessage: string;

        switch (currentFeedbackType) {
            case 'request':
                successTitle = skillRequestSuccessTitleLabel;
                successMessage = skillRequestSuccessMessageLabel;
                break;
            case 'report':
                successTitle = skillReportSuccessTitleLabel;
                successMessage = skillReportSuccessMessageLabel;
                break;
            case 'change':
                successTitle = skillContentReportSuccessTitleLabel;
                successMessage = skillContentReportSuccessMessageLabel;
                break;
            default:
                successTitle = provideFeedbackTitleLabel;
                successMessage = provideFeedbackDescriptionLabel;
                break;
        }

        showFeedbackSuccessToast(successTitle, successMessage);
    };

    return (
        <Modal
            open={isOpen}
            slotProps={{
                backdrop: {
                    timeout: 0,
                },
            }}
            onClose={closeFeedbackModal}
        >
            <ModalContainer>
                <HeaderContainer>
                    <Title>{provideFeedbackTitleLabel}</Title>
                    <Description>{provideFeedbackDescriptionLabel}</Description>
                </HeaderContainer>
                <ContentContainer>
                    <TabsSwitcher activeTab={currentFeedbackType} onTabChange={setFeedbackType} />
                    <TabContent />
                </ContentContainer>
                <ActionButtons canSubmit={canSubmit} onCancel={closeFeedbackModal} onSubmit={onSubmit} />
            </ModalContainer>
        </Modal>
    );
};
